/**
 * Audit package helpers.
 *
 * A ticket that is re-run (info added, or triggered again after FAILED) keeps
 * every persisted audit row. Without grouping, the second attempt's rows sit
 * under the first attempt's FAILED row as if the failure never happened.
 */

const RESTART_STAGE = 'EXTRACTING';

function detailsOf(event) {
  return event && typeof event.details === 'object' && event.details ? event.details : {};
}

function timeOf(event) {
  const raw = event?.timestamp || event?.at || detailsOf(event).at;
  const ms = raw ? new Date(raw).getTime() : NaN;
  return Number.isFinite(ms) ? ms : null;
}

/** A transition back to EXTRACTING from an earlier stage starts a new run. */
export function isAuditRunRestart(event) {
  const details = detailsOf(event);
  const to = String(details.to_stage || '').toUpperCase();
  if (to !== RESTART_STAGE) return false;
  const from = String(details.from_stage || '').trim();
  return from !== '' && from.toUpperCase() !== RESTART_STAGE;
}

/**
 * Split persisted audit events into runs, oldest first.
 * Each run: { index, rerun, restartedFrom, startedAt, endedAt, finalStage, events }.
 */
export function groupAuditEventsByRun(events) {
  const rows = (Array.isArray(events) ? events : []).filter(Boolean);
  const sorted = rows
    .map((event, position) => ({ event, position, time: timeOf(event) }))
    .sort((left, right) => {
      if (left.time != null && right.time != null && left.time !== right.time) {
        return left.time - right.time;
      }
      return left.position - right.position;
    })
    .map((row) => row.event);

  const runs = [];
  let current = null;
  for (const event of sorted) {
    if (!current || isAuditRunRestart(event)) {
      current = {
        index: runs.length + 1,
        rerun: runs.length > 0,
        restartedFrom: runs.length > 0 ? detailsOf(event).from_stage || null : null,
        startedAt: event.timestamp || event.at || null,
        endedAt: null,
        finalStage: null,
        events: [],
      };
      runs.push(current);
    }
    current.events.push(event);
    current.endedAt = event.timestamp || event.at || current.endedAt;
    const to = detailsOf(event).to_stage;
    if (to) current.finalStage = String(to);
  }
  return runs;
}

/** "Run 2 · re-run after FAILED · ended PENDING_APPROVAL" */
export function describeAuditRun(run) {
  if (!run) return '';
  const parts = [`Run ${run.index}`];
  if (run.rerun) {
    parts.push(run.restartedFrom ? `re-run after ${run.restartedFrom}` : 're-run');
  }
  if (run.finalStage) parts.push(`ended ${run.finalStage}`);
  return parts.join(' · ');
}
